import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../data'
import { Modal } from './kit'

/**
 * "We want 25 arms by the 20th."
 *
 * Three fields, and the third is the one people want to skip: **need-by is
 * required**. Every date on the purchaser's buy list is this one minus a lead
 * time, so an order without it cannot be scheduled or bought for — it is a wish
 * rather than a plan, and the buy list would silently drop it.
 *
 * It opens as a draft. Releasing is a second, deliberate tap, because release
 * is the valve between planning and the floor.
 */
export function NewBuildOrder({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient()
  const [revisionId, setRevisionId] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [needBy, setNeedBy] = useState('')
  const [note, setNote] = useState('')
  const [problem, setProblem] = useState('')

  const revisions = useQuery({
    queryKey: ['buildable-revisions'],
    queryFn: () => repository.buildableRevisions(),
  })

  const create = useMutation({
    mutationFn: () =>
      repository.createBuildOrder({
        revisionId,
        quantity: Number(quantity),
        needBy: new Date(`${needBy}T00:00:00Z`).toISOString(),
        note,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['build-orders'] })
      onClose()
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const options = revisions.data ?? []
  const chosen = options.find((r) => r.id === revisionId)
  const count = Number(quantity)
  const ready = revisionId !== '' && Number.isInteger(count) && count >= 1 && needBy !== ''

  return (
    <Modal
      title="Order a build"
      subtitle="It opens as a draft. Nothing reaches the floor or the buy list until you release it."
      onClose={onClose}
      footer={
        <button className="primary" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          Create draft
        </button>
      }
    >
      <div className="stack">
        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">What to build</span>
          <select value={revisionId} onChange={(e) => setRevisionId(e.currentTarget.value)}>
            <option value="">Pick a revision…</option>
            {options.map((r) => (
              <option key={r.id} value={r.id}>
                {r.typeName} {r.name}
                {r.hasBuildProcedure ? '' : ' — no build procedure'}
              </option>
            ))}
          </select>
        </label>

        {revisions.isSuccess && options.length === 0 ? (
          <span className="warn">
            Nothing is buildable yet. A build order needs a type that is made here, with a revision
            and a bill of materials — a designer creates those in Catalog.
          </span>
        ) : null}

        {chosen && !chosen.hasBuildProcedure ? (
          <span className="warn">
            {chosen.typeName} {chosen.name} has no build procedure, so this draft cannot be released
            until a designer writes one.
          </span>
        ) : null}

        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">How many</span>
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={quantity}
            onChange={(e) => setQuantity(e.currentTarget.value)}
          />
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Wanted by</span>
          <input type="date" value={needBy} onChange={(e) => setNeedBy(e.currentTarget.value)} />
          <span className="muted">
            The buy list works backwards from this: a 35-day part for this date has to be ordered
            five weeks before it.
          </span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Note</span>
          <input
            value={note}
            placeholder="optional"
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </label>

        {problem ? <p className="critical" role="alert">{problem}</p> : null}
      </div>
    </Modal>
  )
}
